import type { ComponentType } from "react";
import { useParams } from "wouter";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  type IntegrationConfigProps,
  type IntegrationProviderComponent,
  type IntegrationStatusProps,
} from "./integration_registry.js";

export interface ZernioChannelComponentOptions {
  platform: string;
  label: string;
  Icon: ComponentType;
  ConfigEditor: ComponentType<IntegrationConfigProps>;
}

function ZernioStatusDisplay({ connected, error }: IntegrationStatusProps) {
  if (connected) {
    return <Badge variant="default">Connected</Badge>;
  }
  return <Badge variant="destructive">{error ?? "Not connected"}</Badge>;
}

export function makeZernioChannelComponent(
  options: ZernioChannelComponentOptions,
): IntegrationProviderComponent {
  return {
    type: `zernio_${options.platform}`,
    kind: "channel",
    label: options.label,
    icon: options.Icon,
    ConfigEditor: options.ConfigEditor,
    StatusDisplay: ZernioStatusDisplay,
  };
}

export function InstagramConfigEditor({ config }: IntegrationConfigProps) {
  const { orgSlug, integrationSlug } = useParams<{
    orgSlug: string;
    integrationSlug: string;
  }>();
  const accountId = config.accountId as string | undefined;
  const identifier = (config.identifier as string | undefined) ?? "";
  const connectHref = `/api/o/${orgSlug}/connect/${integrationSlug}/`;

  return (
    <div className="space-y-4">
      {accountId ? (
        <p className="text-sm">
          Connected as{" "}
          <span className="font-medium">@{identifier || accountId}</span>.
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">
          Instagram requires a Business or Creator account linked to a Facebook
          Page. The Connect button below opens Meta's OAuth flow via Zernio.
        </p>
      )}
      <div className="flex gap-2">
        <Button asChild size="sm">
          <a href={connectHref}>
            {accountId ? "Reconnect Instagram" : "Connect Instagram"}
          </a>
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        After authorising on Meta, you'll be redirected back here. Hit Save to
        persist the connection.
      </p>
    </div>
  );
}

export function BlueskyConfigEditor({
  config,
  update,
}: IntegrationConfigProps) {
  const identifier = (config.identifier as string) ?? "";
  const appPassword = (config.appPassword as string) ?? "";
  const connectedId = config.accountId as string | undefined;

  return (
    <div className="space-y-4">
      {connectedId && (
        <p className="text-sm">
          Connected as <span className="font-medium">@{identifier}</span>. Enter
          new credentials below to re-connect.
        </p>
      )}
      <div className="space-y-2">
        <Label htmlFor="identifier">Bluesky handle</Label>
        <Input
          id="identifier"
          value={identifier}
          onChange={e => update({ ...config, identifier: e.target.value })}
          placeholder="yourhandle.bsky.social"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="appPassword">App password</Label>
        <Input
          id="appPassword"
          type="password"
          value={appPassword}
          onChange={e => update({ ...config, appPassword: e.target.value })}
          placeholder="xxxx-xxxx-xxxx-xxxx"
        />
        <p className="text-xs text-muted-foreground">
          Create one at{" "}
          <a
            href="https://bsky.app/settings/app-passwords"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Bluesky &rsaquo; Settings &rsaquo; App Passwords
          </a>
          .
        </p>
      </div>
    </div>
  );
}
